"use client";

import {
  creditSlateForDurationDays,
  MAX_LOCK_AMOUNT_RAW,
  MIN_LOCK_AMOUNT_RAW,
  trialSizeFor,
  USDC_SCALE,
} from "@decibeltrade/sdk";
import { Button } from "@decibeltrade/web-core/components";
import { ModalContent, ModalRenderProps, openModal } from "@decibeltrade/web-core/modal";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { useEffect, useRef } from "react";
import { css } from "styled-system/css";
import { stack } from "styled-system/patterns";

import { useFftFlowStore } from "@/app/(main)/rewards/first-trade-on-us/hooks/use-fft-flow-store";
import { ModalCloseButton } from "@/components/modal/modal-close-button";
import { toast } from "@/components/toast";
import { useAnalyticsRef } from "@/contexts/analytics-context";
import { useLockUsdc } from "@/contexts/mutations/funded-trial/lock";
import { useFundedTrialEligibilityQuery } from "@/contexts/queries/funded-trial/eligibility";
import { useUsdcBalanceQuery } from "@/contexts/queries/usdc-balance";
import { useDecibelWallet } from "@/hooks/use-decibel-wallet";
import { useLockCountdown } from "@/hooks/use-lock-countdown";
import type { Analytics } from "@/lib/analytics";
import { isFftLockWithdrawalQueuedError, isFftRelockDisabledError } from "@/utils/fft-errors";
import { Logger } from "@/utils/logger";
import { NumberFormatter } from "@/utils/number-formatter";

import { FFT_DETAIL_HREF } from "./constants";
import { DynamicOutputCard } from "./dynamic-output-card";
import { FftCreditsUsedAlert } from "./fft-credits-used-alert";
import { LeversSummary } from "./levers-summary";
import { LockAmountField } from "./lock-amount-field";
import { LockDurationButtons } from "./lock-duration-buttons";
import {
  lockAvailableRaw,
  lockFundingSourceFor,
  usePrimarySubaccountAvailableRaw,
} from "./lock-funding";
import { LockFundingRow } from "./lock-funding-row";
import { expiryMsToTrialMinutes } from "./trial-duration";

const MODAL_ID = "ftou-lock-modal";

export function openFtouLockModal(analytics: Analytics) {
  openModal({
    id: MODAL_ID,
    renderContent: (modalProps) => <FtouLockModal {...modalProps} />,
  });
  analytics.ui.modalOpened({ modalName: MODAL_ID });
}

type LockModalVariant = "default" | "creditsUsed" | "noFunds";

function FtouLockModal({ close }: ModalRenderProps) {
  const t = useTranslations("firstTradeOnUs");
  const tLevers = useTranslations("firstTradeOnUs.levers");
  const tModal = useTranslations("firstTradeOnUs.lockModal");
  const router = useRouter();

  const { accountAddress } = useDecibelWallet();
  const eligibilityQuery = useFundedTrialEligibilityQuery({ account: accountAddress });
  const balanceQuery = useUsdcBalanceQuery({ accountAddr: accountAddress });
  const { pendingLockAmount, setPendingLockAmount, pendingDurationDays, setPendingDurationDays } =
    useFftFlowStore();
  const lockMutation = useLockUsdc();

  const eligibility = eligibilityQuery.data;
  const usdcBalanceRaw =
    balanceQuery.data !== undefined
      ? BigInt(Math.floor(balanceQuery.data * USDC_SCALE))
      : undefined;
  const subaccountAvailableRaw = usePrimarySubaccountAvailableRaw();
  const availableRaw =
    usdcBalanceRaw !== undefined && subaccountAvailableRaw !== undefined
      ? lockAvailableRaw(usdcBalanceRaw, subaccountAvailableRaw)
      : undefined;

  const creditsRemaining = eligibility
    ? eligibility.creditsGranted - eligibility.creditsUsed
    : null;
  const isLocked = (eligibility?.activeLockCount ?? 0) > 0;
  const minLockAmount = eligibility?.minLockAmount ?? MIN_LOCK_AMOUNT_RAW;

  const variant: LockModalVariant =
    isLocked && creditsRemaining !== null && creditsRemaining <= 0
      ? "creditsUsed"
      : !isLocked && availableRaw !== undefined && availableRaw < minLockAmount
        ? "noFunds"
        : "default";

  const fundingSource = lockFundingSourceFor(
    pendingLockAmount,
    usdcBalanceRaw ?? BigInt(0),
    subaccountAvailableRaw ?? BigInt(0),
  );

  const lockCountdown = useLockCountdown(eligibility?.activeLockUnlockAtMs ?? null);

  const displayAmount = isLocked
    ? (eligibility?.activeLockedAmount ?? BigInt(0))
    : pendingLockAmount;
  const projectedCredits = isLocked
    ? (eligibility?.creditsGranted ?? 0)
    : creditSlateForDurationDays(pendingDurationDays, eligibility?.tierSlate).credits;
  const displayDurationDays =
    isLocked && eligibility ? eligibility.maxActiveDurationDays : pendingDurationDays;
  const trialMinutes = eligibility ? expiryMsToTrialMinutes(eligibility.expiryMs) : 1;
  const tradePositionUsd =
    Number(
      trialSizeFor(
        displayAmount,
        displayDurationDays,
        eligibility?.payoutAnchors,
        eligibility?.tierSlate,
      ),
    ) / USDC_SCALE;

  const ctaDisabled =
    variant !== "default" ||
    !eligibility ||
    eligibility.locksPaused ||
    availableRaw === undefined ||
    pendingLockAmount < minLockAmount ||
    pendingLockAmount > availableRaw ||
    isLocked ||
    lockMutation.isPending;

  // Close carries abandon (submitted: false) vs conversion, for minLock calibration.
  const analyticsRef = useAnalyticsRef();
  const closePropsRef = useRef({
    submitted: false,
    hadInput: false,
    amountUsd: 0,
    durationDays: pendingDurationDays,
  });
  useEffect(() => {
    closePropsRef.current.amountUsd = Number(pendingLockAmount) / USDC_SCALE;
    closePropsRef.current.durationDays = pendingDurationDays;
  });
  useEffect(() => {
    const analytics = analyticsRef;
    const closeProps = closePropsRef;
    return () => analytics.current.fft.lockModalClosed(closeProps.current);
  }, [analyticsRef]);

  const onAmountChange = (amount: bigint) => {
    closePropsRef.current.hadInput = true;
    const capped = availableRaw !== undefined && amount > availableRaw ? availableRaw : amount;
    setPendingLockAmount(capped);
  };

  useEffect(() => {
    if (
      !isLocked &&
      availableRaw !== undefined &&
      availableRaw >= minLockAmount &&
      pendingLockAmount > availableRaw
    ) {
      setPendingLockAmount(availableRaw);
    }
  }, [isLocked, availableRaw, minLockAmount, pendingLockAmount, setPendingLockAmount]);
  const onDurationChange = (n: number) => {
    closePropsRef.current.hadInput = true;
    setPendingDurationDays(n);
  };

  const onLock = () => {
    void lockMutation
      .mutateAsync({
        amount: pendingLockAmount,
        durationDays: pendingDurationDays,
        fundingSource,
        projectedCredits,
        source: "hub_modal",
      })
      .then(() => {
        closePropsRef.current.submitted = true;
        close();
        // The only next action after locking is spending the credit — land
        // there instead of back on the hub.
        router.push(FFT_DETAIL_HREF);
      })
      .catch((e: unknown) => {
        if (isFftRelockDisabledError(e)) {
          toast({
            variant: "error",
            title: t("errors.lockFailed"),
            description: t("errors.relockDisabled"),
          });
          return;
        }
        if (isFftLockWithdrawalQueuedError(e)) {
          Logger.error(e, { message: "FFT lock aborted: withdrawal queued", feature: "fft" });
          toast({
            variant: "error",
            title: t("errors.lockFailed"),
            description: t("errors.lockWithdrawalQueued"),
          });
          return;
        }
        toast({
          variant: "error",
          title: t("errors.lockFailed"),
          description: e instanceof Error ? e.message : "",
        });
      });
  };

  return (
    <ModalContent className={modalContentStyles}>
      <div className={stack({ gap: "16" })} data-testid="ftou-lock-modal" data-variant={variant}>
        <ModalCloseButton close={close} />
        <h2 className={titleStyles}>{t("pageTitle")}</h2>
        <p className={introStyles}>{tModal("intro")}</p>

        {variant === "creditsUsed" && (
          <FftCreditsUsedAlert
            lockCountdown={lockCountdown}
            learnMoreHref={FFT_DETAIL_HREF}
            onLearnMore={close}
          />
        )}

        <div className={leversBoxStyles}>
          <p className={helperTextStyles}>{tLevers("helper")}</p>
          <LockAmountField
            amount={displayAmount}
            onAmountChange={onAmountChange}
            minLockAmount={minLockAmount}
            maxLockAmount={eligibility?.payoutAnchors.highLock ?? MAX_LOCK_AMOUNT_RAW}
            disabled={isLocked}
            hideSlider={isLocked}
            label={isLocked ? tLevers("lockedAmountLabel") : undefined}
            labelEnd={
              !isLocked && availableRaw !== undefined
                ? tModal("available", {
                    amount: NumberFormatter.usdc(Number(availableRaw) / USDC_SCALE),
                  })
                : undefined
            }
          />
          {variant === "default" && !isLocked && <LockFundingRow source={fundingSource} />}
          {variant === "noFunds" && <p className={noFundsHintStyles}>{tModal("noFundsHint")}</p>}
          <LockDurationButtons
            durationDays={displayDurationDays}
            onDurationChange={onDurationChange}
            disabled={isLocked}
            tierSlate={eligibility?.tierSlate}
          />
        </div>

        <DynamicOutputCard
          creditsRemaining={
            variant === "creditsUsed" ? 0 : isLocked ? (creditsRemaining ?? 0) : projectedCredits
          }
          creditsTotal={projectedCredits}
          tradePositionUsd={tradePositionUsd}
        />

        <LeversSummary
          lockAmountLabel={NumberFormatter.usdc(Number(displayAmount) / USDC_SCALE, {
            decimals: Number.isInteger(Number(displayAmount) / USDC_SCALE) ? 0 : 2,
          })}
          durationLabel={tLevers("durationDays", { days: displayDurationDays })}
          trialMinutes={trialMinutes}
        />

        <div className={stack({ gap: "8", align: "center" })}>
          <Button
            variant="primary"
            size="md"
            fullWidth
            loading={lockMutation.isPending}
            disabled={ctaDisabled}
            onClick={onLock}
            data-testid="ftou-lock-modal-cta"
          >
            {variant === "creditsUsed" ? tModal("ctaNoCredits") : tLevers("ctaLock")}
          </Button>
          <Button variant="text" size="md" onClick={close} data-testid="ftou-lock-modal-cancel">
            {variant === "creditsUsed" ? tModal("close") : tModal("cancel")}
          </Button>
        </div>
      </div>
    </ModalContent>
  );
}

const modalContentStyles = css({
  w: { base: "full", sm: "[451px]" },
  p: { base: "16", sm: "32" },
  rounded: "350",
});

const titleStyles = css({ textStyle: "heading.xs", color: "text.primary" });

const introStyles = css({ textStyle: "body.md", color: "text.tertiary" });

const helperTextStyles = css({ textStyle: "highlight.md", color: "text.primary" });

const noFundsHintStyles = css({ textStyle: "body.sm", color: "text.tertiary" });

const leversBoxStyles = stack({
  gap: "16",
  p: "12",
  rounded: "350",
  border: "1px solid",
  borderColor: "border.primary",
});
